import { t } from '@transifex/native';

import { flashes } from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { create as modelsCreate } from 'pkg/api/models/actions';
import { fullName } from 'pkg/api/models/user/fmt';
import * as sdk from 'pkg/core/sdk';
import { withSdk } from 'pkg/core/with_sdk';

interface CreateUserLogPayload {
	userId: number;
	groupId: number;
	content: string;
}

export async function create(
	organizationId: number,
	user: models.user.User,
	group: models.group.Group,
	payload: CreateUserLogPayload
): Promise<models.userLog.UserLog> {
	const [request, response] = await modelsCreate<
		CreateUserLogPayload,
		models.userLog.UserLog
	>(endpoints.UserLogs.Create(organizationId, user.id), payload);

	if (!request.ok) {
		flashes.show({
			title: t('Failed to save log'),
		});

		return null;
	}

	flashes.show({
		title: t('Created user note'),
		message: t(`Added a user note on {user} in {group}`, {
			user: fullName(user),
			group: group.name,
		}),
	});

	return response;
}

interface UpdateUserLogPayload {
	content: string;
}

export async function update(
	organizationId: number,
	userLog: models.userLog.UserLog,
	payload: UpdateUserLogPayload
): Promise<models.userLog.UserLog> {
	return withSdk<models.userLog.UserLog, UpdateUserLogPayload>(
		'patch',
		endpoints.UserLogs.Update(organizationId, userLog.userId, userLog.id),
		{
			onSuccess() {
				flashes.show({
					title: t('Updated user note'),
					message: t(`Successfully updated user note.`),
				});
			},
		}
	)(payload);
}

type RemoveUserLogCallback = (userLog: models.userLog.UserLog) => void;

export async function remove(
	organizationId: number,
	userLog: models.userLog.UserLog,
	onRemove: RemoveUserLogCallback
): Promise<void> {
	withSdk(
		'delete',
		endpoints.UserLogs.Delete(organizationId, userLog.userId, userLog.id),
		{
			onSuccess: onRemove,
		}
	)();
}

export async function removeAttachment(
	organizationId: number,
	userLog: models.userLog.UserLog,
	attachmentId: number
): Promise<void> {
	withSdk(
		'delete',
		endpoints.UserLogs.RemoveAttachment(
			organizationId,
			userLog.userId,
			userLog.id,
			attachmentId
		)
	)();
}

export async function addAttachments(
	organizationId: number,
	userLog: models.userLog.UserLog,
	attachments: models.attachment.Attachment[]
): Promise<void> {
	await Promise.allSettled(
		attachments.map(async (attachment) => {
			return await sdk.post(
				endpoints.UserLogs.AddAttachment(
					organizationId,
					userLog.userId,
					userLog.id,
					attachment.id
				)
			);
		})
	);
}
